import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

dayjs.extend(advancedFormat);

/**
 * Compute relative time string using native Date.
 * Uses "X min ago", "X hours ago", "today", "yesterday", then "X days ago", etc.
 */
function relativeTimeAgo(dateStr) {
    const created = new Date(dateStr).getTime();
    const now = Date.now();
    if (Number.isNaN(created)) return null;

    const diffMs = now - created;
    const secs = Math.floor(Math.abs(diffMs) / 1000);
    const mins = Math.floor(Math.abs(diffMs) / 60000);
    const hours = Math.floor(Math.abs(diffMs) / 3600000);
    const days = Math.floor(Math.abs(diffMs) / 86400000);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    const createdDate = new Date(dateStr);
    const today = new Date();
    const isToday =
        createdDate.getDate() === today.getDate() &&
        createdDate.getMonth() === today.getMonth() &&
        createdDate.getFullYear() === today.getFullYear();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
        createdDate.getDate() === yesterday.getDate() &&
        createdDate.getMonth() === yesterday.getMonth() &&
        createdDate.getFullYear() === yesterday.getFullYear();

    if (isToday) {
        if (secs < 60) return 'a few seconds ago';
        if (mins < 60) return mins === 1 ? '1 min ago' : `${mins} mins ago`;
        return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }
    if (isYesterday) return 'yesterday';
    if (days < 30) return days === 1 ? 'a day ago' : `${days} days ago`;
    if (months < 12) return months === 1 ? 'a month ago' : `${months} months ago`;
    return years === 1 ? 'a year ago' : `${years} years ago`;
}

/**
 * Past relative, no parentheses (e.g. "15 mins ago", "2 hours ago", "2 days ago").
 * Used by detail panels.
 */
export const formatCreatedRelative = (dateStr) => {
    if (!dateStr) return '—';
    const ago = relativeTimeAgo(dateStr);
    return ago != null ? ago : '—';
};

/**
 * Past relative in parentheses for table columns (e.g. "(15 mins ago)", "(2 days ago)").
 */
export const formatCreated = (dateStr) => {
    if (!dateStr) return '—';
    const ago = relativeTimeAgo(dateStr);
    return ago != null ? `${ago}` : '—';
};

/**
 * Pretty date e.g. "27th Feb 2026".
 */
export const formatLastUpdated = (dateStr) => (dateStr ? dayjs(dateStr).format('Do MMM YYYY') : '—');

/**
 * Date + time label: "September 12, 2026 | 12:00 PM"
 */
export const formatLongDateTimeWithPipe = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return '—';
        const date = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        return `${date} | ${time}`;
    } catch {
        return '—';
    }
};

/** e.g. "Created at September 12, 2026 | 12:00 PM" */
export const formatCreatedAtSentence = (dateStr) => {
    if (!dateStr) return '—';
    const inner = formatLongDateTimeWithPipe(dateStr);
    return inner === '—' ? '—' : `Created at ${inner}`;
};

export const formatCreatedAtSentenceStable = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = dayjs(dateStr);
        if (!d.isValid()) return '—';
        return `Created at ${d.format('MMMM D, YYYY | h:mm A')}`;
    } catch {
        return '—';
    }
};

export const formatAssignedAtSentenceStable = (dateStr) => {
    if (!dateStr) return null;
    try {
        const d = dayjs(dateStr);
        if (!d.isValid()) return null;
        return `Assigned at ${d.format('MMMM D, YYYY | h:mm A')}`;
    } catch {
        return null;
    }
};

/**
 * Same time label pattern as main Chat (today: time only; else date + time).
 */
export function formatChatMessageTime(createdAt) {
    if (!createdAt) return '';
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
